/**
 * <Button> — the canonical interactive primitive.
 *
 * Contract (UI-SPEC + CONTEXT):
 *   - CVA-typed variants: `variant` (primary/secondary/destructive/ghost/link),
 *     `size` (sm/md/lg/icon), `density` (comfortable/compact).
 *     Exhaustively typed per D-06 — `variant="invalid"` fails to compile.
 *   - `size="icon"` enforces a 44×44px min touch target (UI-SPEC §Spacing
 *     "Touch-target floor" / EW-17 / WCAG 2.5.5). It ALSO requires a non-empty
 *     `aria-label` of type `NonEmptyString` — icon-only controls without an
 *     accessible name are a 4.1.2 violation, and the branded type makes the
 *     omission a *compile* failure rather than a runtime axe complaint.
 *   - `asChild` uses Radix Slot (D-07): `<Button asChild><Link to="/">Go</Link></Button>`
 *     merges button classes onto the `<Link>` without nesting DOM.
 *   - Visual state relies on semantic tokens only (bg-primary, text-on-primary,
 *     bg-surface-sunken, text-default, border-subtle, bg-danger, focus-ring,
 *     etc.). No hex literals, no raw color scales.
 *   - Logical properties only (`ps-*`/`pe-*`/`gap-*` — never `pl-*`/`pr-*`).
 *
 * Primary vs Secondary vs Destructive vs Ghost vs Link per UI-SPEC §Color rule 1
 * (primary reserved for primary CTA fill).
 */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn, cva, type VariantProps } from "../../utils";
import type { NonEmptyString } from "../../types/NonEmptyString";

// ---------------------------------------------------------------------------
// CVA variants
// ---------------------------------------------------------------------------

export const buttonVariants = cva(
  // Base — applies to every variant. Focus ring, disabled styling, logical
  // padding layout, transitions, typography role.
  cn(
    "inline-flex items-center justify-center gap-sm",
    "rounded-md text-body font-semibold whitespace-nowrap",
    // Transition scoped to the properties that actually change on hover/press/
    // focus — color, background, border, transform, outline. Avoids `transition-all`
    // (which would tween layout props + thrash). transform is included for the
    // press micro-state below.
    "transition-[color,background-color,border-color,transform,outline-color] duration-fast ease-out",
    // Press micro-state — a 2% scale-down on active gives a tactile "click"
    // without moving neighbours. Disabled buttons don't react. Collapses under
    // reduced-motion (the transform transition duration is already ~0 there via
    // --duration-fast, and the scale is imperceptible at that speed).
    "active:scale-[0.98] motion-reduce:active:scale-100",
    "disabled:opacity-50 disabled:pointer-events-none disabled:active:scale-100",
    // Focus ring — the 2px outline-offset is a documented UI-SPEC exception.
    "focus-visible:focus-ring",
    // Reset browser defaults inherited by <button>.
    "appearance-none border border-transparent",
  ),
  {
    variants: {
      variant: {
        primary: cn(
          "bg-primary text-on-primary shadow-primary",
          "hover:brightness-110 active:brightness-95",
        ),
        secondary: cn(
          "bg-surface-elevated text-default border border-subtle",
          "hover:bg-surface-sunken active:bg-surface-sunken",
        ),
        destructive: cn("bg-danger text-white", "hover:brightness-110 active:brightness-95"),
        ghost: cn(
          "bg-transparent text-default",
          "hover:bg-surface-sunken active:bg-surface-sunken",
        ),
        link: cn("bg-transparent text-primary", "underline-offset-4 hover:underline"),
      },
      size: {
        sm: "h-8 px-sm text-label",
        md: "h-10 px-md",
        lg: "h-12 px-lg",
        // Icon size — 44×44 per UI-SPEC "Touch-target floor" (WCAG 2.5.5 / EW-17).
        // Hardcoded inline with rationale per UI-SPEC §Spacing: NOT a spacing token.
        icon: "min-h-[44px] min-w-[44px] p-sm",
      },
      density: {
        comfortable: "",
        // Compact subtracts one step from Y-axis padding on sm/md; never applies
        // to icon (44px floor preserved).
        compact: "",
      },
    },
    // Density applies only to non-icon sizes (UI-SPEC: "Never applies to icon-only targets").
    compoundVariants: [
      { size: "sm", density: "compact", class: "h-7" },
      { size: "md", density: "compact", class: "h-9 px-sm" },
      { size: "lg", density: "compact", class: "h-11 px-md" },
    ],
    defaultVariants: {
      variant: "primary",
      size: "md",
      density: "comfortable",
    },
  },
);

// ---------------------------------------------------------------------------
// Prop types — discriminated union enforces NonEmptyString aria-label on icon.
// ---------------------------------------------------------------------------

type ButtonCvaProps = VariantProps<typeof buttonVariants>;

type ButtonBaseProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> & {
  /**
   * Render as a child element (e.g. `<Link>`) via Radix Slot (D-07).
   * Child must accept `className` and forward it; single-root only.
   */
  asChild?: boolean;
  /** Variant — accent-filled primary, outlined secondary, destructive, etc. */
  variant?: ButtonCvaProps["variant"];
  /** Density — `compact` subtracts one step from Y-axis padding. */
  density?: ButtonCvaProps["density"];
};

// Icon-only variant REQUIRES a non-empty accessible name.
type ButtonIconOnlyProps = ButtonBaseProps & {
  size: "icon";
  "aria-label": NonEmptyString;
};

// Text variants: size defaults to "md"; aria-label optional (label comes from children).
type ButtonTextProps = ButtonBaseProps & {
  size?: "sm" | "md" | "lg";
  "aria-label"?: string;
};

export type ButtonProps = ButtonIconOnlyProps | ButtonTextProps;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  const {
    asChild = false,
    variant,
    size,
    density,
    className,
    type,
    children,
    ...rest
  } = props as ButtonBaseProps & {
    size?: ButtonCvaProps["size"];
    className?: string;
  };

  const Comp = asChild ? Slot : "button";

  // Keep the rendered DOM a real <button> (with an explicit type to avoid
  // accidental form submits) when not using asChild. When asChild, the Slot
  // will merge classes into the consumer's child element.
  const buttonTypeProps = asChild || type !== undefined ? {} : { type: "button" as const };

  return (
    <Comp
      ref={ref as React.Ref<HTMLButtonElement>}
      className={cn(buttonVariants({ variant, size, density }), className)}
      {...buttonTypeProps}
      {...(type !== undefined ? { type } : {})}
      {...rest}
    >
      {children}
    </Comp>
  );
});
